/**
 * Servicio para guardar feedback del usuario directamente en Supabase
 * Permite múltiples feedbacks por conversación con un contador
 */

import conversationSaverService from './conversationSaverService';
import supabaseService from './supabaseService';

export interface FeedbackData {
  internal_interaction_id: string; // ID de la conversación
  feedback_count: number; // Número de retroalimentación (1, 2, 3, etc.)
  rating: number; // 1-5
  feedback_text?: string; // Texto opcional del usuario
}

export class FeedbackService {
  /**
   * Obtener el siguiente número de feedback para una conversación
   */
  private async getNextFeedbackCount(internalInteractionId: string): Promise<number> {
    try {
      const client = supabaseService.getClient();
      const { data, error } = await client
        .from('feedback')
        .select('feedback_count')
        .eq('internal_interaction_id', internalInteractionId)
        .order('feedback_count', { ascending: false })
        .limit(1);

      if (error) {
        console.error('[FeedbackService] Error al obtener feedbacks existentes:', error);
        // Si hay error, asumir que es el primero
        return 1;
      }

      if (!data || data.length === 0) {
        // No hay feedbacks previos, este es el primero
        return 1;
      }

      // Retornar el siguiente número
      return (data[0].feedback_count || 0) + 1;
    } catch (error) {
      console.error('[FeedbackService] Error al calcular siguiente feedback count:', error);
      // En caso de error, asumir que es el primero
      return 1;
    }
  }

  /**
   * Guardar feedback directamente en Supabase
   * Calcula automáticamente el número de feedback para la conversación
   */
  async sendFeedback(rating: number, feedbackText: string): Promise<void> {
    // Obtener el ID de la conversación actual
    const internalInteractionId = conversationSaverService.getCurrentInteractionId();
    
    if (!internalInteractionId) {
      throw new Error('No hay una conversación activa para asociar el feedback');
    }

    // Obtener el siguiente número de feedback para esta conversación
    const feedbackCount = await this.getNextFeedbackCount(internalInteractionId);

    const feedbackData: FeedbackData = {
      internal_interaction_id: internalInteractionId,
      feedback_count: feedbackCount,
      rating,
      feedback_text: feedbackText.trim() || undefined,
    };

    try {

      const client = supabaseService.getClient();
      const { data, error } = await client
        .from('feedback')
        .insert([feedbackData])
        .select();

      if (error) {
        throw new Error(`Error al guardar feedback: ${error.message}`);
      }

    } catch (error) {
      console.error('[FeedbackService] Error al guardar feedback:', error);
      throw error;
    }
  }
}

export const feedbackService = new FeedbackService();
export default feedbackService;
